import { useCallback, useRef, useState } from 'react';
import type { TableRowProps } from '@chakra-ui/react';

/** Avoid native row-drag when the gesture starts on editable fields, actions, or open menus. */
export function shouldSuppressInvoicePartTableRowDrag(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    return !!target.closest(
        'input, textarea, select, button, [role="menu"], [role="menuitem"], [role="menuitemradio"]'
    );
}

export interface UseInvoicePartTableRowDragOptions {
    isReadOnly: boolean;
    onReorder: (fromIndex: number, toIndex: number) => void;
}

/**
 * HTML5 drag-and-drop for reordering invoice part grid rows (line items, freight, misc, ignored).
 * Same behavior across tabs: blue insertion bar, grab cursor, suppress drag from inputs/buttons.
 */
export function useInvoicePartTableRowDrag({ isReadOnly, onReorder }: UseInvoicePartTableRowDragOptions) {
    const onReorderRef = useRef(onReorder);
    onReorderRef.current = onReorder;

    const [dragFrom, setDragFrom] = useState<number | null>(null);
    /** Insertion index (0..rowCount) shown by blue bar during drag. */
    const [dragInsertIndex, setDragInsertIndex] = useState<number | null>(null);

    const getDraggableRowProps = useCallback(
        (rowIndex: number): TableRowProps => ({
            opacity: dragFrom === rowIndex ? 0.65 : 1,
            boxShadow:
                dragFrom != null && dragFrom !== rowIndex
                    ? dragInsertIndex === rowIndex
                        ? 'inset 0 3px 0 0 var(--chakra-colors-blue-400)'
                        : dragInsertIndex === rowIndex + 1
                          ? 'inset 0 -3px 0 0 var(--chakra-colors-blue-400)'
                          : undefined
                    : undefined,
            draggable: !isReadOnly,
            sx: !isReadOnly ? { cursor: dragFrom === rowIndex ? 'grabbing' : 'grab' } : undefined,
            onDragStart: (e) => {
                if (isReadOnly || shouldSuppressInvoicePartTableRowDrag(e.target)) {
                    e.preventDefault();
                    return;
                }
                e.stopPropagation();
                e.dataTransfer.setData('text/plain', String(rowIndex));
                e.dataTransfer.effectAllowed = 'move';
                setDragFrom(rowIndex);
            },
            onDragEnd: () => {
                setDragFrom(null);
                setDragInsertIndex(null);
            },
            onDragOver: (e) => {
                if (isReadOnly || dragFrom == null) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                const rect = e.currentTarget.getBoundingClientRect();
                const y = e.clientY - rect.top;
                const dropAfter = y > rect.height / 2;
                setDragInsertIndex(dropAfter ? rowIndex + 1 : rowIndex);
            },
            onDrop: (e) => {
                if (isReadOnly) return;
                e.preventDefault();
                const raw = e.dataTransfer.getData('text/plain');
                const from = parseInt(raw, 10);
                if (Number.isNaN(from)) return;
                const insertIndex = dragInsertIndex ?? rowIndex;
                const dropTo = from < insertIndex ? insertIndex - 1 : insertIndex;
                onReorderRef.current(from, dropTo);
                setDragFrom(null);
                setDragInsertIndex(null);
            },
        }),
        [dragFrom, dragInsertIndex, isReadOnly]
    );

    return { dragFrom, dragInsertIndex, getDraggableRowProps };
}
